import { useEffect, useRef, useState } from "react";
import { workspaceRequest } from "@/services/firebase/artistWorkspace";
import { Button } from "@/components/ui/button";

interface Job {
  stage: string; errorCode: string | null; polls: number; attempt: number;
  deliveryAttempt: number; cmsPreviewReady: boolean; cmsPreviewError: string | null;
  cleanupStatus: string | null; cleanupError: string | null; receiptsKnown: boolean;
  references: {draft: boolean; published: boolean};
}
interface Inspection {allowed: boolean; confirmation: string; waitUntil: number; references: {draft: boolean; published: boolean}}
interface QueueItem {jobId: string; assetId: string; contentId: string; stage: string; errorCode: string | null; ageMs: number}

/** Staff controls operate on a bound asset/job, never an arbitrary provider Vid. */
export function ArtistVlogOperations({artistId, contentId, assetId}: {artistId: string; contentId: string; assetId: string}) {
  const [job, setJob] = useState<Job>();
  const [inspection, setInspection] = useState<Inspection>();
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const epoch = useRef(0);
  useEffect(() => () => {epoch.current++;}, [artistId, contentId, assetId]);
  async function run(action: string, extra: Record<string, unknown> = {}) {
    if (busy) return;
    const request = ++epoch.current;
    setBusy(true); setError(undefined);
    try {
      const result = await workspaceRequest<Inspection>(action, artistId, {contentId, assetId, ...extra});
      if (request !== epoch.current) return;
      setInspection(action === "vlogCleanupInspect" ? result : undefined);
      const current = await workspaceRequest<Job>("vlogJobGet", artistId, {contentId, assetId});
      if (request === epoch.current) setJob(current);
    } catch (e) {if (request === epoch.current) setError(e instanceof Error ? e.message : "操作未完成");}
    finally {if (request === epoch.current) setBusy(false);}
  }
  return <section aria-label="Vlog 維運" className="space-y-3 rounded border p-3">
    <p className="font-medium">影片處理與維運</p>
    {job && <div className="text-sm space-y-1">
      <p>工作階段：{job.stage} · 匯入 {job.attempt ?? 0} 次 · 播放交付 {job.deliveryAttempt ?? 0} 次 · 輪詢 {job.polls ?? 0} 次</p>
      <p>審核副本：{job.cmsPreviewReady ? "已完成" : "未完成"}{job.cmsPreviewError ? `（${job.cmsPreviewError}）` : ""}</p>
      <p>引用：{job.references?.draft ? "草稿使用中" : "未被草稿使用"} · {job.references?.published ? "公開版本使用中" : "未被公開版本使用"}</p>
      {job.errorCode && <p>錯誤：{job.errorCode}</p>}
      {job.cleanupStatus && <p>清理：{job.cleanupStatus} {job.cleanupError}</p>}
    </div>}
    {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
    <div className="flex flex-wrap gap-2">
      <Button className="h-auto min-h-10 max-w-full whitespace-normal" variant="outline" disabled={busy} onClick={() => void run("vlogJobGet")}>查看工作狀態</Button>
      <Button className="h-auto min-h-10 max-w-full whitespace-normal" variant="outline" disabled={busy || !job || !["ready", "transcoded", "failed"].includes(job.stage)} onClick={() => void run("vlogDeliveryRetry")}>重試播放交付／審核副本</Button>
      <Button className="h-auto min-h-10 max-w-full whitespace-normal" variant="outline" disabled={busy || job?.stage !== "reconcile" || !job.receiptsKnown} onClick={() => void run("vlogJobReconcile")}>核對已記錄的處理結果</Button>
      <Button className="h-auto min-h-10 max-w-full whitespace-normal" variant="outline" disabled={busy || !job || job.references?.published || ["ready", "deleted", "cancelled"].includes(job.stage)} onClick={() => void run("vlogJobCancel")}>停止此工作</Button>
      <Button className="h-auto min-h-10 max-w-full whitespace-normal" variant="outline" disabled={busy} onClick={() => void run("vlogCleanupInspect")}>檢查清理條件</Button>
      {job?.cleanupStatus === "failed" && <Button className="h-auto min-h-10 max-w-full whitespace-normal" variant="outline" disabled={busy} onClick={() => void run("vlogCleanupRetry")}>重試已確認的清理</Button>}
    </div>
    {inspection && <div role="region" aria-label="清理檢查結果" className="space-y-2 text-sm">
      <p>{inspection.allowed ? "此影片已解除引用並超過授權保護期。" : "目前不能清理：仍被引用、工作尚未結束，或尚在授權保護期。"}</p>
      <p>保護期至：{new Date(inspection.waitUntil).toLocaleString()}</p>
      {inspection.allowed && <><p>確認後將刪除此影片的原片、審核副本與 VOD 成品，無法復原。封面保留。</p>
        <Button className="h-auto min-h-10 max-w-full whitespace-normal" variant="destructive" disabled={busy} onClick={() => void run("vlogCleanup", {confirmation: inspection.confirmation})}>確認清理這支影片</Button></>}
    </div>}
  </section>;
}

export function ArtistVlogQueue({artistId}: {artistId: string}) {
  const [items, setItems] = useState<QueueItem[]>();
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const epoch = useRef(0);
  useEffect(() => () => {epoch.current++;}, [artistId]);
  async function refresh() {
    const request = ++epoch.current; setBusy(true); setError(undefined);
    try {
      const result = await workspaceRequest<{items: QueueItem[]}>("vlogJobsList", artistId);
      if (request === epoch.current) setItems(result.items);
    } catch (e) {if (request === epoch.current) setError(e instanceof Error ? e.message : "清單未完成");}
    finally {if (request === epoch.current) setBusy(false);}
  }
  const counts = items?.reduce<Record<string, number>>((all, j) => ({...all, [j.stage]: (all[j.stage] ?? 0) + 1}), {});
  return <section aria-label="Vlog 工作監控" className="space-y-2 rounded border p-3">
    <Button className="h-auto min-h-10 max-w-full whitespace-normal" variant="outline" disabled={busy} onClick={() => void refresh()}>查看最近 100 筆影片工作</Button>
    {error && <p role="alert">{error}</p>}
    {counts && <p className="text-sm break-words">{Object.entries(counts).map(([stage, count]) => `${stage}：${count}`).join(" · ")}</p>}
    {items?.filter(j => !["ready", "deleted", "cancelled"].includes(j.stage)).slice(0, 10).map(j => <p className="text-xs break-all" key={j.jobId}>{j.contentId} · {j.stage} · 已建立 {Math.round(j.ageMs / 60000)} 分鐘 {j.errorCode}</p>)}
    {items && <p className="text-xs text-muted-foreground">僅統計最近更新的 100 筆工作，非全部歷史總數。</p>}
  </section>;
}

export function ArtistVlogCoverCleanup({artistId, contentId, assetId}: {artistId: string; contentId: string; assetId: string}) {
  const [inspection, setInspection] = useState<Inspection & {cleanupStatus?: string}>();
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const epoch = useRef(0);
  useEffect(() => () => {epoch.current++;}, [artistId, contentId, assetId]);
  async function run(action: string, confirmation?: string) {
    const request = ++epoch.current; setBusy(true); setError(undefined);
    try {
      await workspaceRequest(action, artistId, {contentId, assetId, ...(confirmation ? {confirmation} : {})});
      const result = await workspaceRequest<Inspection>("vlogCleanupInspect", artistId, {contentId, assetId});
      if (request === epoch.current) setInspection(result);
    } catch (e) {if (request === epoch.current) setError(e instanceof Error ? e.message : "操作未完成");}
    finally {if (request === epoch.current) setBusy(false);}
  }
  return <section aria-label="Vlog 封面清理" className="space-y-2 rounded border p-3 text-sm">
    <Button className="h-auto min-h-10 max-w-full whitespace-normal" variant="outline" disabled={busy} onClick={() => void run("vlogCleanupInspect")}>檢查封面清理條件</Button>
    {error && <p role="alert">{error}</p>}
    {inspection && <p>{inspection.allowed ? "此封面已解除引用並超過保留期。" : "封面仍被引用、尚在保留期，或未記錄可安全刪除的版本。"}</p>}
    {inspection?.allowed && <><p>確認後將永久刪除此封面，影片保持不變。</p><Button className="h-auto min-h-10 max-w-full whitespace-normal" variant="destructive" disabled={busy}
      onClick={() => void run("vlogCleanup", inspection.confirmation)}>確認清理這張封面</Button></>}
    {inspection?.cleanupStatus === "failed" && <Button className="h-auto min-h-10 max-w-full whitespace-normal" variant="outline" disabled={busy} onClick={() => void run("vlogCleanupRetry")}>重試已確認的封面清理</Button>}
  </section>;
}
